import React, { useState } from 'react';
import { ArrowRight, Eye, KeyRound, Plus, PlugZap, RefreshCw, Users } from 'lucide-react';
import { AppChrome, BrandLogo } from './AppChrome';
import { SectionIntro } from './SectionIntro';

/**
 * The claim of this pillar is oversight: one place that shows which company
 * tools are reachable, and which AI surfaces can reach them. A catalogue of
 * 4,000 logos does not say that — a map of tool → surface does.
 */

type SurfaceId = 'web' | 'slack' | 'chatgpt' | 'claude' | 'grok';

// Third-party surfaces first, Giga's own last: the list reads as "everywhere
// else, plus the two places Giga itself runs".
const SURFACES: Array<{
  id: SurfaceId;
  name: string;
  slug: string;
  note: string;
}> = [{
  id: 'chatgpt',
  name: 'ChatGPT',
  slug: 'chatgpt',
  // Kept short: these sit in a fixed-width panel, so a longer note truncates.
  note: 'Company-wide'
}, {
  id: 'claude',
  name: 'Claude Code',
  slug: 'claude',
  note: 'Engineering only'
}, {
  id: 'grok',
  name: 'Grok bot',
  slug: 'grok',
  note: 'Growth only'
}, {
  // Giga is listed as one surface, not as "for web" and "for Slack" — the
  // point is that it sits at the same level as the third-party surfaces.
  id: 'web',
  name: 'Giga',
  slug: 'giga',
  note: 'All tools'
}];
/**
 * Every mark in this screen resolves through one component, so optical sizing
 * (ChatGPT's knot in particular) and Giga's own mark are handled in a single
 * place. The previous special case forced Giga's mark to pure black with a
 * brightness filter, which flattened it against brand-coloured logos.
 */
function SurfaceIcon({
  slug,
  className
}: {
  slug: string;
  className: string;
}) {
  return <BrandLogo slug={slug} className={className} data-id="element-385" data-mp="2bk5iu9fq442z" />;
}
type Node = {
  slug: string;
  name: string;
  accounts: number;
  surfaces: SurfaceId[];
};

// Workspace-wide view: every connected tool, how many accounts sit behind it,
// and which AI surfaces are allowed to use it.
const BY_SURFACE: Node[] = [{
  slug: 'slack',
  name: 'Slack',
  accounts: 3,
  surfaces: ['chatgpt', 'claude', 'web', 'slack']
}, {
  slug: 'gmail',
  name: 'Gmail',
  accounts: 5,
  surfaces: ['chatgpt', 'web', 'slack']
}, {
  slug: 'intercom',
  name: 'Intercom',
  accounts: 4,
  surfaces: ['chatgpt', 'web', 'slack']
}, {
  slug: 'salesforce',
  name: 'Salesforce',
  accounts: 2,
  surfaces: ['chatgpt', 'grok', 'web']
}, {
  slug: 'linear',
  name: 'Linear',
  accounts: 3,
  surfaces: ['claude', 'web', 'slack']
}, {
  slug: 'snowflake',
  name: 'Snowflake',
  accounts: 1,
  surfaces: ['claude', 'web']
}, {
  slug: 'hubspot',
  name: 'HubSpot',
  accounts: 2,
  surfaces: ['grok', 'web', 'slack']
}];
type Member = {
  name: string;
  role: string;
  initials: string;
  tint: string;
  nodes: Node[];
};
const TEAM: Member[] = [{
  name: 'Sarah Chen',
  role: 'Head of Ops · Admin',
  initials: 'SC',
  tint: '#134E4A',
  nodes: [{
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'gmail',
    name: 'Gmail',
    accounts: 2,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'snowflake',
    name: 'Snowflake',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }]
}, {
  name: 'Marcus Lee',
  role: 'Engineering',
  initials: 'ML',
  tint: '#3F3F46',
  nodes: [{
    slug: 'linear',
    name: 'Linear',
    accounts: 1,
    surfaces: ['claude', 'web', 'slack']
  }, {
    slug: 'github',
    name: 'GitHub',
    accounts: 2,
    surfaces: ['claude', 'web']
  }, {
    slug: 'sentry',
    name: 'Sentry',
    accounts: 1,
    surfaces: ['claude', 'slack']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Priya Raman',
  role: 'Growth',
  initials: 'PR',
  tint: '#7C2D12',
  nodes: [{
    slug: 'hubspot',
    name: 'HubSpot',
    accounts: 2,
    surfaces: ['grok', 'web', 'slack']
  }, {
    slug: 'mailchimp',
    name: 'Mailchimp',
    accounts: 1,
    surfaces: ['grok', 'web']
  }, {
    slug: 'ahrefs',
    name: 'Ahrefs',
    accounts: 1,
    surfaces: ['grok', 'chatgpt']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Tom Okafor',
  role: 'Finance',
  initials: 'TO',
  tint: '#1E3A5F',
  nodes: [{
    slug: 'stripe',
    name: 'Stripe',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'quickbooks',
    name: 'QuickBooks',
    accounts: 1,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'gmail',
    name: 'Gmail',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }]
}, {
  name: 'Dana Whitfield',
  role: 'Support',
  initials: 'DW',
  tint: '#4C1D95',
  nodes: [{
    slug: 'intercom',
    name: 'Intercom',
    accounts: 2,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'linear',
    name: 'Linear',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}];
// The middle cut of the same map. A person's connections answer "what can
// Sarah reach"; a group's answer "what can this team reach" — which is the
// question an admin actually asks when granting or revoking access, since
// access is granted by group rather than person by person.
type Group = {
  name: string;
  color: string;
  members: number;
  nodes: Node[];
};
const GROUPS: Group[] = [{
  name: 'Sales',
  color: '#134E4A',
  members: 7,
  nodes: [{
    slug: 'salesforce',
    name: 'Salesforce',
    accounts: 2,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'gmail',
    name: 'Gmail',
    accounts: 3,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'slack',
    name: 'Slack',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'granola',
    name: 'Granola',
    accounts: 2,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Marketing',
  color: '#7C2D12',
  members: 6,
  nodes: [{
    slug: 'hubspot',
    name: 'HubSpot',
    accounts: 2,
    surfaces: ['grok', 'web', 'slack']
  }, {
    slug: 'mailchimp',
    name: 'Mailchimp',
    accounts: 1,
    surfaces: ['grok', 'web']
  }, {
    slug: 'ahrefs',
    name: 'Ahrefs',
    accounts: 1,
    surfaces: ['grok', 'chatgpt']
  }, {
    slug: 'figma',
    name: 'Figma',
    accounts: 2,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Customer Success',
  color: '#3F3A2E',
  members: 4,
  nodes: [{
    slug: 'intercom',
    name: 'Intercom',
    accounts: 4,
    surfaces: ['chatgpt', 'web', 'slack']
  }, {
    slug: 'linear',
    name: 'Linear',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    accounts: 1,
    surfaces: ['web', 'slack']
  }]
}, {
  name: 'Engineering',
  color: '#3F3F46',
  members: 9,
  nodes: [{
    slug: 'github',
    name: 'GitHub',
    accounts: 2,
    surfaces: ['claude', 'web']
  }, {
    slug: 'linear',
    name: 'Linear',
    accounts: 3,
    surfaces: ['claude', 'web', 'slack']
  }, {
    slug: 'sentry',
    name: 'Sentry',
    accounts: 1,
    surfaces: ['claude', 'slack']
  }, {
    slug: 'snowflake',
    name: 'Snowflake',
    accounts: 1,
    surfaces: ['claude', 'web']
  }]
}, {
  name: 'Leadership',
  color: '#1E3A5F',
  members: 5,
  nodes: [{
    slug: 'stripe',
    name: 'Stripe',
    accounts: 1,
    surfaces: ['web', 'slack']
  }, {
    slug: 'quickbooks',
    name: 'QuickBooks',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }, {
    slug: 'salesforce',
    name: 'Salesforce',
    accounts: 1,
    surfaces: ['chatgpt', 'web']
  }, {
    slug: 'snowflake',
    name: 'Snowflake',
    accounts: 1,
    surfaces: ['web']
  }]
}];
const POINTS = [{
  icon: PlugZap,
  title: "Works with everything they already use",
  copy: '4,000 tools in the directory, plus databases, API keys, and email-and-password logins for the ones without an API.'
}, {
  icon: KeyRound,
  title: 'Connected once, not per product',
  copy: 'Credentials live in Giga, not inside ChatGPT or Claude, so no single AI provider controls how your team reaches its tools.'
}, {
  icon: Eye,
  title: 'Owned centrally',
  copy: 'Every connection has an owner, a group, and a history. Admins can see what is connected and where company data can flow.'
}, {
  icon: RefreshCw,
  title: 'Kept alive',
  copy: 'Token refreshes are handled for you, and a one-click reconnect link goes out the moment something drops.'
}];
/**
 * The Connections screen itself, without the section around it. Split out so
 * the export page can render exactly the same screen standalone.
 */
export function ConnectionsScreen() {
  const [tab, setTab] = useState<'surface' | 'group' | 'person'>('surface');
  const [memberIndex, setMemberIndex] = useState(0);
  const [groupIndex, setGroupIndex] = useState(0);
  const nodes = tab === 'surface' ? BY_SURFACE : tab === 'group' ? GROUPS[groupIndex].nodes : TEAM[memberIndex].nodes;
  const surfaces = tab === 'surface' ? SURFACES : SURFACES.filter(s => nodes.some(n => n.surfaces.includes(s.id)));
  return <div className="px-6 py-6 md:px-9 md:py-8" data-id="element-386" data-mp="vdtzwzwf5pm0">
      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-387" data-mp="vf56f7h91bgt">
        <div data-id="element-388" data-mp="1abcnw019vyse">
          <h3 className="font-title text-[24px] font-semibold leading-tight tracking-[-0.015em] text-[#2B211A]" data-id="element-389" data-mp="1a7yl4qop5nkf">
            Connections
          </h3>
          {/* States the job of the screen rather than defining the concept —
           this one is a management surface, not a primitive to introduce. */}
          <p className="mt-1 text-[13px] text-neutral-500" data-id="element-390" data-mp="1e9sgcjg4hzd7">
            Manage which tools are connected and which AI can reach them
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[12.5px] font-bold text-white shadow-[0_1px_2px_rgba(43,33,26,0.3),0_8px_18px_-10px_rgba(43,33,26,0.7)]" data-id="element-391" data-mp="1o2r644ncvvye">
          <Plus size={13} strokeWidth={2.6} data-id="element-392" data-mp="1y1714ipi7pd6" />
          Connect new tool
        </span>
      </div>

      <div className="mb-6 mt-7 flex items-center gap-6 border-b border-[#EFEAE1]" data-id="element-393" data-mp="3nz36eh6j030j">
        {/* Widest cut first, then narrowing: everything → a team → one
         person. */}
        {([['surface', 'By surface'], ['group', 'By group'], ['person', 'By person']] as const).map(([id, label]) => <button key={id} type="button" onClick={() => setTab(id)} className={`-mb-px border-b-2 pb-3 text-[13px] font-semibold transition-colors ${tab === id ? 'border-[#2B211A] text-[#2B211A]' : 'border-transparent text-neutral-400 hover:text-[#2B211A]'}`} data-id="element-394" data-mp="399xab22ziswv">
              {label}
            </button>)}
      </div>

      {/* Group and person are the same map with a filter on top, not
       different screens — so the mental model carries across all three tabs.
       Group tiles are squared and carry the people glyph, matching how a group
       is drawn on the Groups screen. */}
      {tab === 'group' && <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-395" data-mp="2hgn17paid60g">
          {GROUPS.map((g, i) => <button key={g.name} type="button" onClick={() => setGroupIndex(i)} aria-pressed={i === groupIndex} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${i === groupIndex ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#E7E0D4] text-[#4A3E35] hover:border-[#2B211A]/35'}`} data-id="element-396" data-mp="x06yaxpcajwi" data-mpi={i}>
              <span className="flex size-4 items-center justify-center rounded-[5px]" style={{
          backgroundColor: i === groupIndex ? 'rgba(255,255,255,0.22)' : g.color
        }} data-id="element-397" data-mp="1fpvoyem5re62">
                <Users size={9} strokeWidth={2.4} className="text-white" data-id="element-398" data-mp="1kqt40ibvfs3c" />
              </span>
              {g.name}
              <span className={`tabular-nums ${i === groupIndex ? 'text-white/55' : 'text-neutral-400'}`} data-id="element-399" data-mp="1tpeju5pykt0f">
                {g.members}
              </span>
            </button>)}
        </div>}

      {tab === 'person' && <div className="mb-4 flex flex-wrap gap-1.5" data-id="element-400" data-mp="3vghuguzum7kz">
          {TEAM.map((m, i) => <button key={m.name} type="button" onClick={() => setMemberIndex(i)} aria-pressed={i === memberIndex} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12px] font-semibold transition-colors ${i === memberIndex ? 'border-[#2B211A] bg-[#2B211A] text-white' : 'border-[#E7E0D4] text-[#4A3E35] hover:border-[#2B211A]/35'}`} data-id="element-401" data-mp="3viujtt7xrk82" data-mpi={i}>
              <span className="flex size-4 items-center justify-center rounded-full text-[8px] font-bold text-white" style={{
          backgroundColor: i === memberIndex ? 'rgba(255,255,255,0.22)' : m.tint
        }} data-id="element-402" data-mp="16ml5d5jb9ef6">
                {m.initials}
              </span>
              {m.name.split(' ')[0]}
            </button>)}
        </div>}

      <AccessMap key={tab === 'surface' ? 'all' : tab === 'group' ? `g-${GROUPS[groupIndex].name}` : TEAM[memberIndex].name} nodes={nodes} surfaces={surfaces} data-id="element-403" data-mp="1qc3tnz85ciio" />
    </div>;
}
export function ConnectedAppsPillar() {
  return <div data-id="element-404" data-mp="1zdbsqjihlndt">
      <SectionIntro className="mx-auto max-w-3xl text-center" data-id="element-405" data-mp="1zdlaxkxaiq8y">
        <p className="mb-4 text-[12px] font-bold uppercase tracking-[0.14em] text-[#EA580C]" data-id="element-406" data-mp="2iyls2jo2wi84">
          Connected Apps
        </p>
        <h2 className="font-title text-4xl font-bold leading-[1.05] tracking-tight text-[#2B211A] md:text-[56px]" data-id="element-407" data-mp="2nxyg37qorvca">
          Connect your tools once.
          <br data-id="element-408" data-mp="292if0irouz6e" />
          <span className="text-[#EA580C]" data-id="element-409" data-mp="2dzimmy46s9zm">Use them everywhere.</span>
        </h2>
        <p className="mx-auto mt-5 max-w-[52ch] text-lg leading-relaxed text-[#6B5D52]" data-id="element-410" data-mp="2e1llm7nbhdgh">
          Giga is the connection layer between your team and the tools they're connecting to their AI. They connect tools to Giga, then connect Giga to everything else. 
        </p>
      </SectionIntro>

      <div className="mx-auto mt-12 max-w-[1000px] md:mt-16" data-id="element-411" data-mp="1qbr77lncngu4">
        {/* No app nav. This screen's whole job is the tool-to-surface map, and
         the map needs the width more than a visitor needs to be shown a menu
         they cannot use. Every product screen on the page is now framed the
         same way. */}
        <AppChrome sidebar={false} data-id="element-412" data-mp="1e7nawwi7qsq2">
          <ConnectionsScreen data-id="element-413" data-mp="1e7wtw04ubzm9" />
        </AppChrome>
      </div>

      <div className="mx-auto mt-14 grid max-w-[1000px] gap-x-10 gap-y-8 md:mt-16 md:grid-cols-2" data-id="element-414" data-mp="198k3x6ioacfg">
        {POINTS.map(({
        icon: Icon,
        title,
        copy
      }) => <div key={title} className="flex gap-4" data-id="element-415" data-mp="14g0oe40fd780">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl border border-[#EA580C]/25 bg-white text-[#EA580C]" data-id="element-416" data-mp="zjipvlvou544">
              <Icon size={17} data-id="element-417" data-mp="vigqn51rtt25" />
            </span>
            <div data-id="element-418" data-mp="qhj7ap50ysz2">
              <h3 className="font-title text-xl font-bold leading-snug text-[#2B211A]" data-id="element-419" data-mp="qh10iykqkwcw">
                {title}
              </h3>
              <p className="mt-1.5 text-[15px] leading-relaxed text-[#6B5D52]" data-id="element-420" data-mp="lj7v47r9c6do">
                {copy}
              </p>
            </div>
          </div>)}
      </div>
    </div>;
}
// Taller rows, wider gutter. The map's job is oversight, and oversight reads as
// calm — a tight grid of rows reads as a database table instead.
const ROW_H = 60;
const ROW_GAP = 10;
const STEP = ROW_H + ROW_GAP;
const CONNECTOR_W = 132;
/** Inset of the surfaces panel, so its rows clear the panel's own edge. */
const PANEL_PAD = 12;
type Focus = {
  kind: 'tool';
  slug: string;
} | {
  kind: 'surface';
  id: SurfaceId;
} | null;

/** Tools on the left, AI surfaces on the right, lines for what reaches what. */
function AccessMap({
  nodes,
  surfaces
}: {
  nodes: Node[];
  surfaces: Array<(typeof SURFACES)[number]>;
}) {
  const [focus, setFocus] = useState<Focus>(null);
  const listH = (n: number) => n * STEP - ROW_GAP;
  // One column each. The tools stack is the taller side, so it sets the map's
  // height and the surfaces panel is centred against it — the panel spans the
  // full height, which is what stops the shorter side reading as adrift.
  const height = Math.max(listH(nodes.length), listH(surfaces.length) + PANEL_PAD * 2);
  const toolY = (i: number) => (height - listH(nodes.length)) / 2 + i * STEP + ROW_H / 2;
  const surfaceY = (i: number) => height / 2 + (i - (surfaces.length - 1) / 2) * STEP;
  const isLive = (slug: string, id: SurfaceId) => {
    if (!focus) return true;
    if (focus.kind === 'tool') return focus.slug === slug;
    return focus.id === id;
  };
  const toolDim = (slug: string) => focus?.kind === 'tool' ? focus.slug !== slug : focus?.kind === 'surface' ? !nodes.find(n => n.slug === slug)?.surfaces.includes(focus.id) : false;
  const surfaceDim = (id: SurfaceId) => focus?.kind === 'surface' ? focus.id !== id : focus?.kind === 'tool' ? !nodes.find(n => n.slug === focus.slug)?.surfaces.includes(id) : false;
  return <div data-id="element-421" data-mp="2r6esr2qlk2ud">
      {/* Both headings sit in one row above the map, on a shared baseline, with
       the direction of the relationship stated between them. */}
      <div className="mb-3 hidden items-baseline md:flex" data-id="element-422" data-mp="2mdlnmdr7ushr">
        <p className="flex flex-1 items-baseline gap-2 text-[10.5px] font-bold uppercase tracking-[0.12em] text-neutral-400" data-id="element-423" data-mp="1yokmzcomkln1">
          Connected tools
          <span className="text-[11px] font-semibold tabular-nums tracking-normal text-neutral-300" data-id="element-424" data-mp="98qgluogf8h9">
            {nodes.length}
          </span>
        </p>
        {/* Just the arrow in the gutter now. "Reachable from" was reading as a
         third column heading beside the real one, which is what made this row
         confusing — it is the relationship, so it belongs on the column it
         describes. */}
        <p className="flex shrink-0 items-center justify-center text-neutral-300" style={{
        width: CONNECTOR_W
      }} data-id="element-425" data-mp="rypzu0xm5tcr">
          <ArrowRight size={13} strokeWidth={2.4} data-id="element-426" data-mp="wwzvsdr5wxzf" />
        </p>
        <p className="flex shrink-0 items-baseline gap-2 pl-3 text-[10.5px] font-bold uppercase tracking-[0.12em] text-neutral-400 md:w-[268px]" data-id="element-427" data-mp="iytfgbi9bgni">
          Reachable from
          <span className="text-[11px] font-semibold tabular-nums tracking-normal text-neutral-300" data-id="element-428" data-mp="n27v312a9v0f">
            {surfaces.length}
          </span>
        </p>
      </div>

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-0" data-id="element-429" data-mp="37rxbvjmjrqvr">
      <ul className="flex-1 space-y-2.5" onMouseLeave={() => setFocus(null)} data-id="element-430" data-mp="39m1i579ee9u">
        {nodes.map(n => <li key={n.slug} data-id="element-431" data-mp="3uekosep77ljs">
            <button type="button" onMouseEnter={() => setFocus({
            kind: 'tool',
            slug: n.slug
          })} onFocus={() => setFocus({
            kind: 'tool',
            slug: n.slug
          })} onClick={() => setFocus(f => f?.kind === 'tool' && f.slug === n.slug ? null : {
            kind: 'tool',
            slug: n.slug
          })} className={`flex w-full items-center gap-3.5 rounded-xl border px-4 text-left transition-[background-color,border-color,box-shadow,opacity] duration-200 ease-out ${focus?.kind === 'tool' && focus.slug === n.slug ? 'border-[#EA580C]/45 bg-[#FFF8F3] shadow-[0_2px_10px_-4px_rgba(234,88,12,0.28)]' : 'border-[#EFEAE1] bg-white shadow-[0_1px_2px_-1px_rgba(43,33,26,0.06)]'} ${toolDim(n.slug) ? 'opacity-35' : ''}`} style={{
            height: ROW_H
          }} data-id="element-432" data-mp="3udas9txzulf9">
              {/* The logo gets its own tile so wildly different marks — square
               favicons, round ones, wordmarks — sit on a common footprint. */}
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-[#F1ECE3] bg-[#FCFAF6]" data-id="element-433" data-mp="1d5r7cornv4ax">
                <BrandLogo slug={n.slug} className="size-[18px]" data-id="element-434" data-mp="1i42hz1ps5iu7" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-435" data-mp="1m80h3cf3utn9">
                <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-436" data-mp="1r6bqxn5eh45h">
                  {n.name}
                </span>
                <span className="block truncate text-[11.5px] tabular-nums text-neutral-500" data-id="element-437" data-mp="1w4da082tt3gy">
                  {n.accounts} connected{' '}
                  {n.accounts === 1 ? 'account' : 'accounts'}
                </span>
              </span>
              {/* Only the third-party surfaces are listed here. Giga itself can
               always reach every connection, so its two marks added no
               information — and at this size they read as black dots. */}
              <span className="flex shrink-0 items-center -space-x-1.5" data-id="element-438" data-mp="3ft1q4caxgnw5">
                {n.surfaces.map(id => SURFACES.find(s => s.id === id)).filter((s): s is (typeof SURFACES)[number] => Boolean(s) && s!.slug !== 'giga').map(s => <SurfaceIcon key={s.id} slug={s.slug} className="size-[22px] rounded-full bg-white p-[3px] ring-1 ring-[#EFEAE1]" data-id="element-439" data-mp="3os59lhe0b5b6" />)}
              </span>
            </button>
          </li>)}
      </ul>

      {/* Fixed row heights let the connectors be drawn from indices, so no
       measurement pass is needed and the lines never drift. */}
      <div className="hidden shrink-0 md:block" style={{
        width: CONNECTOR_W
      }} aria-hidden="true" data-id="element-440" data-mp="7iyr7b3mvhvw">
        <svg width={CONNECTOR_W} height={height} viewBox={`0 0 ${CONNECTOR_W} ${height}`} data-id="element-441" data-mp="ce5q85lyjyeh">
          {nodes.map((n, ti) => n.surfaces.map(id => {
            const si = surfaces.findIndex(s => s.id === id);
            if (si < 0) return null;
            const y1 = toolY(ti);
            const y2 = surfaceY(si);
            // Nothing is drawn at rest — every link at once was noise. Lines
            // appear only for the row under the pointer.
            const live = Boolean(focus) && isLive(n.slug, id);
            return <path key={`${n.slug}-${id}`} d={`M0 ${y1} C ${CONNECTOR_W * 0.45} ${y1}, ${CONNECTOR_W * 0.55} ${y2}, ${CONNECTOR_W} ${y2}`} fill="none" stroke="#EA580C" strokeWidth={1.5} strokeLinecap="round" opacity={live ? 1 : 0} style={{
              transition: 'opacity 160ms ease-out'
            }} data-id="element-442" data-mp="qhbq3aezdvaw" />;
          }))}
        </svg>
      </div>

      {/* The panel gives the surfaces side a defined region of its own, so the
       two halves read as peers rather than a long list and a few loose rows. */}
      <ul className="flex flex-col justify-center space-y-2.5 rounded-2xl border border-[#F1ECE3] bg-[#FCFAF6] md:w-[268px]" style={{
        padding: PANEL_PAD,
        minHeight: height
      }} onMouseLeave={() => setFocus(null)} data-id="element-443" data-mp="15ekjsarrbb5s">
        {surfaces.map(s => <li key={s.id} data-id="element-444" data-mp="1x4ytrvwng3c6">
            <button type="button" onMouseEnter={() => setFocus({
            kind: 'surface',
            id: s.id
          })} onFocus={() => setFocus({
            kind: 'surface',
            id: s.id
          })} onClick={() => setFocus(f => f?.kind === 'surface' && f.id === s.id ? null : {
            kind: 'surface',
            id: s.id
          })} className={`flex w-full items-center gap-3.5 rounded-xl border px-4 text-left transition-[background-color,border-color,box-shadow,opacity] duration-200 ease-out ${focus?.kind === 'surface' && focus.id === s.id ? 'border-[#EA580C]/45 bg-[#FFF8F3] shadow-[0_2px_10px_-4px_rgba(234,88,12,0.28)]' : 'border-[#EFEAE1] bg-white shadow-[0_1px_2px_-1px_rgba(43,33,26,0.06)]'} ${surfaceDim(s.id) ? 'opacity-35' : ''}`} style={{
            height: ROW_H
          }} data-id="element-445" data-mp="1x3nqdx748xll">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-[#F1ECE3] bg-[#FCFAF6]" data-id="element-446" data-mp="33q49zxdq9amu">
                <SurfaceIcon slug={s.slug} className="size-[18px]" data-id="element-447" data-mp="11wepv2xdz6hf" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-448" data-mp="2mepakzyj54l8">
                <span className="block truncate text-[13.5px] font-semibold text-[#2B211A]" data-id="element-449" data-mp="2mfh84gbotce5">
                  {s.name}
                </span>
                <span className="block truncate text-[11.5px] tabular-nums text-neutral-500" data-id="element-450" data-mp="2hdrviw8tgqny">
                  {nodes.filter(n => n.surfaces.includes(s.id)).length} tools ·{' '}
                  {s.note}
                </span>
              </span>
            </button>
          </li>)}
      </ul>
      </div>
    </div>;
}